/**
 * Email Verify Dialog Component
 *
 * Two-step dialog: enter/confirm email → enter OTP.
 * Shown when isUpdatedEmail === false after login.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Mail } from "lucide-react";
import { authService, type User } from "@/services/authService";

interface EmailVerifyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  onVerified: () => void;
  /** When true, the dialog can be closed by the user (for profile page use). Default: false (forced on login). */
  dismissible?: boolean;
}

type Step = "email" | "otp";

const RESEND_COOLDOWN_SECONDS = 60;

export function EmailVerifyDialog({
  open,
  onOpenChange,
  user,
  onVerified,
  dismissible = false,
}: EmailVerifyDialogProps) {
  const [step, setStep] = React.useState<Step>("email");
  const [email, setEmail] = React.useState(user.email || "");
  const [otp, setOtp] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [countdown, setCountdown] = React.useState(0);

  // Countdown timer for resend cooldown
  React.useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendOtp = async () => {
    setError("");
    setLoading(true);
    try {
      await authService.sendEmailOtp(email);
      setStep("otp");
      setOtp("");
      setCountdown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal mengirim OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError("");
    setLoading(true);
    try {
      await authService.sendEmailOtp(email);
      setOtp("");
      setCountdown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal mengirim ulang OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    setLoading(true);
    try {
      await authService.verifyEmailOtp(otp);
      onVerified();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal memverifikasi OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep("email");
    setOtp("");
    setError("");
    setCountdown(0);
  };

  return (
    <Dialog open={open} onOpenChange={dismissible ? onOpenChange : () => {}}>
      <DialogContent
        className={`sm:max-w-md${dismissible ? "" : " [&>button:last-child]:hidden"}`}
        onInteractOutside={dismissible ? undefined : (e) => e.preventDefault()}
        onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/20">
              <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <DialogTitle className="text-left">
              {step === "email" ? "Verifikasi Email" : "Masukkan Kode OTP"}
            </DialogTitle>
          </div>
          <DialogDescription className="pt-1 text-left">
            {step === "email"
              ? "Masukkan atau konfirmasi alamat email Anda. Kami akan mengirimkan kode OTP untuk verifikasi."
              : `Kode OTP telah dikirim ke ${email}. Masukkan kode tersebut di bawah ini.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {step === "email" ? (
            <>
              <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <span className="mt-0.5 text-red-600 dark:text-red-400">⚠</span>
                <p className="text-sm font-medium text-red-700 dark:text-red-300">
                  Pastikan email Anda benar, karena akan digunakan untuk verifikasi anggota.
                </p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="email-input">
                  Email
                </label>
                <input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contoh@email.com"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => e.key === "Enter" && !loading && handleSendOtp()}
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loading || !email.trim()}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? "Mengirim..." : "Kirim OTP"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="otp-input">
                  Kode OTP
                </label>
                <input
                  id="otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="1234"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-center text-2xl font-bold tracking-widest ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => e.key === "Enter" && !loading && otp.length === 4 && handleVerifyOtp()}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground mt-1">
                  OTP berlaku selama 10 menit.{" "}
                  {countdown > 0 ? (
                    <span className="text-muted-foreground">
                      Kirim ulang dalam {countdown}d
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={loading}
                      className="text-blue-600 hover:underline dark:text-blue-400 disabled:opacity-50"
                    >
                      Kirim ulang
                    </button>
                  )}
                </p>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleBack}
                  className="rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-accent"
                >
                  Kembali
                </button>
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 4}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? "Memverifikasi..." : "Verifikasi"}
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
